/**
 * @file routes/index.tsx
 * @brief Root route map for the Verblizr Web Panel.
 *
 * @details
 * - Public auth pages live behind `RedirectIfAuthenticated` so already
 *   logged-in users skip them.
 * - `/admin/*`   → super-admin space, guarded by `RequireSuperAdmin`.
 * - `/company/*` → company-admin space, guarded by `RequireCompanyAdmin`.
 * - The bare `/` route routes the user to their role-appropriate home
 *   (or to `/login` when unauthenticated).
 *
 * @dependencies
 * - react-router-dom v6 (createBrowserRouter)
 * - shared/auth/RouteGuards
 *
 * @author DES
 * @date 26-05-2026
 * @updated 30-07-2026 — Company Settings hub replaces last ComingSoon placeholder
 */

import { createBrowserRouter, Navigate } from "react-router-dom";
import { AuthLayout } from "@/layouts/AuthLayout";
import { SuperAdminLayout } from "@/layouts/SuperAdminLayout";
import { CompanyAdminLayout } from "@/layouts/CompanyAdminLayout";
import {
  RedirectIfAuthenticated,
  RequireAuth,
  RequireBoardroomEntitlement,
  RequireCompanyAdmin,
  RequireEmployeeApp,
  RequireNotesEntitlement,
  RequireSuperAdmin,
  RequireWebInterpretation,
} from "@/shared/auth/RouteGuards";
import { LoginPage } from "@/features/auth/LoginPage";
import { ConsentPage } from "@/features/mcp-connect/ConsentPage";
import { MagicLinkPage } from "@/features/auth/magic/MagicLinkPage";
import { ForgotPasswordPage } from "@/features/auth/ForgotPasswordPage";
import { RegisterPersonalPage } from "@/features/auth/register/RegisterPersonalPage";
import { RegisterBusinessPage } from "@/features/auth/register/RegisterBusinessPage";
import { SuperAdminDashboardPage } from "@/features/dashboard/SuperAdminDashboardPage";
import { CompanyAdminDashboardPage } from "@/features/dashboard/CompanyAdminDashboardPage";
import { NotFoundPage } from "@/features/error/NotFoundPage";
import { ForbiddenPage } from "@/features/error/ForbiddenPage";
import { CompanySettingsPage } from "@/features/company-settings/CompanySettingsPage";
import { AsanaIntegrationPage } from "@/features/connectors/AsanaIntegrationPage";
import { IntegrationsPage } from "@/features/integrations/IntegrationsPage";
import { UsersPage } from "@/features/users/UsersPage";
import { CompaniesPage } from "@/features/companies/CompaniesPage";
import { SessionsPage } from "@/features/sessions/SessionsPage";
import { SessionDetailPage } from "@/features/sessions/SessionDetailPage";
import { InterpretPage } from "@/features/interpret/InterpretPage";
import { DEV_INTERPRET_PREVIEW } from "@/features/interpret/dev/previewSwitches";
import {
  InterpretPreviewPage,
  PreviewAuth,
  SpeakerResolvePreviewPage,
} from "@/features/interpret/dev/InterpretPreview";
import { ChartsPreviewPage } from "@/features/analytics/dev/ChartsPreview";
import { AgendaPreviewPage } from "@/features/company-meetings/dev/AgendaPreview";
import { AiConfigPreviewPage, AiSettingsPreviewPage } from "@/features/settings/dev/AiConfigPreview";
import { MeetingNotesPanelPreview } from "@/features/notes/dev/MeetingNotesPanelPreview";
import { AnalyticsPage } from "@/features/analytics/AnalyticsPage";
import { BusinessAnalyticsPreviewPage } from "@/features/business-analytics/dev/BusinessAnalyticsPreview";
import { AdminReportsPage } from "@/features/admin-reports/AdminReportsPage";
import { BillingPage } from "@/features/billing/BillingPage";
import { AdminInvoicesPage } from "@/features/admin-invoices/AdminInvoicesPage";
import { LanguagesPage } from "@/features/languages/LanguagesPage";
import { BannersPage } from "@/features/banners/BannersPage";
import { FeedbackPage } from "@/features/feedback/FeedbackPage";
import { EnterpriseInquiriesPage } from "@/features/enterprise-inquiries/EnterpriseInquiriesPage";
import { ActivityLogPage } from "@/features/activity-log/ActivityLogPage";
import { SettingsPage } from "@/features/settings/SettingsPage";
import { ModulesPage } from "@/features/platform-modules/ModulesPage";
import { ProfilePage } from "@/features/profile/ProfilePage";
import { TermsGate } from "@/features/auth/TermsGate";
import { VoiceEnrollmentPage } from "@/features/voice-enrollment/VoiceEnrollmentPage";
import { VoiceEnrollmentPreviewPage } from "@/features/voice-enrollment/VoiceEnrollmentPreviewPage";
import { RootRedirect } from "./RootRedirect";
import { EmployeesPage } from "@/features/employees/EmployeesPage";
import { CompanySessionsPage } from "@/features/company-sessions/CompanySessionsPage";
import { CompanyBillingPage } from "@/features/company-billing/CompanyBillingPage";
import { CompanyInvoicesPage } from "@/features/company-invoices/CompanyInvoicesPage";
import { CompanySchedulePage } from "@/features/company-schedule/CompanySchedulePage";
import { CompanyProfilePage } from "@/features/company-profile/CompanyProfilePage";
import { CompanyFeaturesPage } from "@/features/company-features/CompanyFeaturesPage";
import { MedicalAssessmentsPage } from "@/features/medical-assessment/MedicalAssessmentsPage";
import { MedicalAssessmentDetailPage } from "@/features/medical-assessment/MedicalAssessmentDetailPage";
import { ClinicSoftwareSettingsPage } from "@/features/medical-assessment/ClinicSoftwareSettingsPage";
import { ReportInstructionsPage } from "@/features/medical-assessment/ReportInstructionsPage";
import { RequireCompanyFeature } from "@/features/company-features/RequireCompanyFeature";
import { RequireUnlockedFeature } from "@/features/company-features/RequireUnlockedFeature";
import { NotesPage } from "@/features/notes/NotesPage";
import { NoteComposePage } from "@/features/notes/NoteComposePage";
import { NoteDetailPage } from "@/features/notes/NoteDetailPage";
import { CompanyMeetingDetailPage } from "@/features/company-meetings/CompanyMeetingDetailPage";
import { AdminMeetingsPage } from "@/features/admin-meetings/AdminMeetingsPage";
import { AdminMeetingDetailPage } from "@/features/admin-meetings/AdminMeetingDetailPage";
import { CompanyAnalyticsPage } from "@/features/company-analytics/CompanyAnalyticsPage";
import { CompanyReportsPage } from "@/features/company-reports/CompanyReportsPage";
import { EmployeeBoardroomLayout } from "@/features/employee-boardroom/EmployeeBoardroomLayout";
import { MeetingsLibraryPage } from "@/features/employee-boardroom/MeetingsLibraryPage";
import { HomePage } from "@/features/home/HomePage";
import { DemoMeetingPage } from "@/features/demo-meeting/DemoMeetingPage";
import { MyTodosPage } from "@/features/todos/MyTodosPage";
import { InboxPage } from "@/features/inbox/InboxPage";
import { EmployeeMeetingDetailPage } from "@/features/employee-boardroom/EmployeeMeetingDetailPage";
import { BoardroomSetupPage } from "@/features/employee-boardroom/BoardroomSetupPage";
import { ScheduleSessionDialog } from "@/features/company-schedule/components/ScheduleSessionDialog";
import { BoardroomRecorderPage } from "@/features/employee-boardroom/BoardroomRecorderPage";
import { ScheduledMeetingsPage } from "@/features/employee-boardroom/ScheduledMeetingsPage";
import { DesktopWidgetPage } from "@/features/desktop-widget/DesktopWidgetPage";
import { DesktopSettingsPage } from "@/features/desktop-settings/DesktopSettingsPage";
import { MyBillingPage } from "@/features/my-billing/MyBillingPage";
import { BillingPreviewPage } from "@/features/my-billing/dev/BillingPreview";
import { FreeMinutesSeed } from "@/features/my-billing/dev/FreeMinutesSeed";
import { PlanPricesPreviewPage } from "@/features/billing/dev/PlanPricesPreview";
import { VendorCostsPreviewPage } from "@/features/billing/dev/VendorCostsPreview";
import { BillingCountriesPreviewPage } from "@/features/billing/dev/BillingCountriesPreview";
import { LinkSettingsPreviewPage } from "@/features/link-settings/dev/LinkSettingsPreview";
import { LinkSettingsPage } from "@/features/link-settings/LinkSettingsPage";
import { IntegrationSwitchesPage } from "@/features/integration-switches/IntegrationSwitchesPage";
import { MacUpdateScreen } from "@/shared/desktop/DesktopUpdateGate";
import { DataSharingPreviewPage } from "@/features/model-training/dev/DataSharingPreview";
import { AiConsentPreviewPage } from "@/features/ai-consent/dev/AiConsentPreview";
import { AccountDeletionPreviewPage } from "@/features/account-deletion/dev/AccountDeletionPreview";
import { PublicDeletionConfirmPreviewPage, PublicDeletionPreviewPage } from "@/features/account-deletion/dev/PublicDeletionPreview";
import { DeleteAccountConfirmPage, DeleteAccountPublicPage } from "@/features/account-deletion/DeleteAccountPublicPage";
import { EmailUndoPage } from "@/features/auth/EmailUndoPage";

// ----------------------------------------------------------------------
// Route map
// ----------------------------------------------------------------------
export const router = createBrowserRouter([
  // Root → redirect based on auth state + role
  {
    path: "/",
    element: <RootRedirect />,
  },

  // The desktop app's floating session widget (DESKTOP/src/widget.js,
  // 02-10-2026): its own small windows, outside every layout and guard. A
  // view of the live session the main window publishes; no data of its own
  // beyond the user's notes, which the API scopes to the signed-in user.
  { path: "/desktop/widget", element: <DesktopWidgetPage /> },
  // The desktop app's Settings window (DESKTOP/src/settings.js, 02-10-2026):
  // settings stored on the machine, no API calls.
  { path: "/desktop/settings", element: <DesktopSettingsPage /> },

  // DEV ONLY (01-10-2026): the interpreter with a stand-in user and a scripted
  // session, inside the real employee shell. Dropped from production builds.
  ...(import.meta.env.DEV
    ? [
        {
          path: "/__preview",
          element: (
            <PreviewAuth>
              <EmployeeBoardroomLayout />
            </PreviewAuth>
          ),
          children: [
            { path: "interpret", element: <InterpretPreviewPage /> },
            // The setup form's layout with the stand-in user (01-10-2026).
            // `?free=ok|warn|blocked|verify` seeds the Free minutes (B4, 22-10-2026).
            { path: "boardroom-setup", element: <FreeMinutesSeed><BoardroomSetupPage /></FreeMinutesSeed> },
            // The Boardroom recorder in its UI preview (no mic, no billing),
            // with the stand-in user (DES 11-10-2026). Open with ?uiPreview=1.
            { path: "boardroom-record", element: <BoardroomRecorderPage /> },
            // Boardrooms with the stand-in user, for the desktop calendar
            // panel (06-10-2026; needs window.verblizrDesktop v8 to show).
            { path: "boardrooms", element: <MeetingsLibraryPage /> },
            // Notes Library with the stand-in user, for the Meeting notes |
            // Personal notes tabs and the one-slide start (07-10-2026).
            { path: "notes", element: <NotesPage /> },
            { path: "speaker-resolve", element: <SpeakerResolvePreviewPage /> },
            // Register your voice with the stand-in user and a seeded status
            // (04-10-2026); `?state=enrolled` for the registered view.
            { path: "voice-enrollment", element: <VoiceEnrollmentPreviewPage /> },
            // Billing with seeded data, a personal stand-in (17-10-2026):
            // ?plan=none|pro|annual|past_due|cancelling&cards=0|1|2. Look,
            // don't press: Pay / Save card / Remove call the real API.
            { path: "billing/:section", element: <BillingPreviewPage /> },
            // Super Admin Subscription plans, per-currency prices (21-10-2026).
            // Look, don't press Save.
            { path: "plan-prices", element: <PlanPricesPreviewPage /> },
            // Super Admin Vendor costs, seeded catalog (22-10-2026). Look,
            // don't press Save.
            { path: "vendor-costs", element: <VendorCostsPreviewPage /> },
            // Super Admin Blocked countries (B1 block-list, 22-10-2026). The
            // route is stubbed in the page, so Save is safe here.
            { path: "billing-countries", element: <BillingCountriesPreviewPage /> },
            // Super Admin Links & company info (B23 / W19, 24-10-2026). Both
            // calls are answered in the page, so Save is safe here.
            { path: "link-settings", element: <LinkSettingsPreviewPage /> },
            // Super Admin Analytics on B25's own test-seed payloads (W22,
            // 25-10-2026): ?state=empty|nullmrr|churn|error|loading|live.
            // Read-only views: nothing leaves the page.
            { path: "business-analytics", element: <BusinessAnalyticsPreviewPage /> },
            // The Mac app's "Please update" screen (W19, 24-10-2026).
            { path: "mac-update", element: <MacUpdateScreen downloadUrl="https://verblizr.com/download/mac/" /> },
            // Home with the stand-in user, for the Ask intro (04-10-2026).
            { path: "home", element: <FreeMinutesSeed><HomePage /></FreeMinutesSeed> },
            // The built-in demo meeting (calm Home, 20-10-2026).
            { path: "meetings/demo", element: <DemoMeetingPage /> },
            // TrendChart loading vs loaded, to check the skeleton (03-10-2026).
            { path: "charts", element: <ChartsPreviewPage /> },
            // Profile with the Delete account section (05-10-2026).
            { path: "account", element: <ProfilePage /> },
            // Profile + Company settings with a seeded "Data & sharing" state
            // (ADR-048, 20-10-2026): ?view=personal|member|admin.
            { path: "data-sharing", element: <DataSharingPreviewPage /> },
            // "How VERBLIZR uses AI" with a stubbed backend (B7, 22-10-2026):
            // ?stale=1 · ?agreed=1. Nothing leaves the page.
            { path: "ai-consent", element: <AiConsentPreviewPage /> },
            // Company deletion: member request, admin Delete company, Employees
            // requests (B7, 22-10-2026). Every DELETE is answered in the page.
            { path: "account-deletion", element: <AccountDeletionPreviewPage /> },
            // Terms + Privacy gate; shows when the stand-in user owes it (06-10-2026).
            { path: "terms", element: <TermsGate /> },
            // Meeting Agenda tab with seeded coverage (05-10-2026).
            { path: "agenda", element: <AgendaPreviewPage /> },
            // AI configuration with seeded settings: per-step meeting models
            // (13-10-2026). `?routing=length_tier` for the rollback view.
            { path: "ai-config", element: <AiConfigPreviewPage /> },
            // The simple AI settings page with seeded rows (15-10-2026). Look,
            // don't save: Save calls the real API.
            { path: "ai-settings", element: <AiSettingsPreviewPage /> },
            // The recorder's notepad in the Write a Note look (07-10-2026). Look,
            // don't type: it saves real notes.
            { path: "meeting-notes-panel", element: <MeetingNotesPanelPreview /> },
            // The Schedule session form, open, to check its pickers.
            {
              path: "schedule-session",
              element: <ScheduleSessionDialog open onOpenChange={() => undefined} mode="create" />,
            },
          ],
        },
      ]
    : []),

  // DEV ONLY (22-10-2026): the public delete-account pages with a stubbed
  // backend, in the sign-in layout. Nothing leaves the page.
  ...(import.meta.env.DEV
    ? [
        {
          path: "/__preview-public",
          element: <AuthLayout />,
          children: [
            { path: "delete-account", element: <PublicDeletionPreviewPage /> },
            { path: "delete-account/confirm", element: <PublicDeletionConfirmPreviewPage /> },
          ],
        },
      ]
    : []),

  // Delete your account without signing in (Google Play, B7 22-10-2026).
  // Outside RedirectIfAuthenticated: it works signed in or out. Linked from
  // the sign-in footer.
  {
    element: <AuthLayout />,
    children: [
      { path: "/delete-account", element: <DeleteAccountPublicPage /> },
      { path: "/delete-account/confirm", element: <DeleteAccountConfirmPage /> },
      // B34 (26-10-2026): undo link in the "email was changed" notice.
      { path: "/auth/email-undo", element: <EmailUndoPage /> },
    ],
  },

  // Public auth routes
  {
    element: (
      <RedirectIfAuthenticated>
        <AuthLayout />
      </RedirectIfAuthenticated>
    ),
    children: [
      { path: "/login", element: <LoginPage /> },
      { path: "/forgot-password", element: <ForgotPasswordPage /> },
      // Sign-up, the phone's register screens (01-10-2026).
      { path: "/register", element: <RegisterPersonalPage /> },
      { path: "/register/business", element: <RegisterBusinessPage /> },
    ],
  },

  // One-tap link from a billing email (18-10-2026, BACKEND A6): signs in as
  // the link's user and opens the Billing screen it names. Outside
  // RedirectIfAuthenticated on purpose (it may switch who is signed in).
  { path: "/auth/magic", element: <MagicLinkPage /> },

  // "Connect Claude / ChatGPT to Verblizr" consent (MCP OAuth, 06-10-2026):
  // any signed-in role, outside every role layout. Login returns here.
  {
    path: "/oauth/consent",
    element: (
      <RequireAuth>
        <ConsentPage />
      </RequireAuth>
    ),
  },

  // Super Admin space
  {
    path: "/admin",
    element: (
      <RequireSuperAdmin>
        <SuperAdminLayout />
      </RequireSuperAdmin>
    ),
    children: [
      { index: true, element: <SuperAdminDashboardPage /> },
      /* A super admin's own Home, as everyone else's (DES 13-10-2026: "superadmin
         ko bhi Home do"). The pages it opens are mounted here too, so its
         slide and links stay in /admin: Inbox, My to-dos, a new / scheduled
         meeting and the recorder. Every list they read is the caller's own;
         their meetings and sessions open in the platform pages below. */
      { path: "home", element: <HomePage /> },
      { path: "inbox", element: <InboxPage /> },
      { path: "todos", element: <MyTodosPage /> },
      {
        path: "meetings/new",
        element: (
          <RequireBoardroomEntitlement redirectTo="/admin/home">
            <BoardroomSetupPage />
          </RequireBoardroomEntitlement>
        ),
      },
      {
        path: "meetings/record",
        element: (
          <RequireBoardroomEntitlement redirectTo="/admin/home">
            <BoardroomRecorderPage />
          </RequireBoardroomEntitlement>
        ),
      },
      {
        path: "meetings/scheduled",
        element: (
          <RequireBoardroomEntitlement redirectTo="/admin/home">
            <ScheduledMeetingsPage />
          </RequireBoardroomEntitlement>
        ),
      },
      { path: "users", element: <UsersPage /> },
      { path: "companies", element: <CompaniesPage /> },
      { path: "sessions", element: <SessionsPage /> },
      // A session as its own page (01-10-2026).
      { path: "sessions/:sessionId", element: <SessionDetailPage scope="platform" /> },
      /* MeetingIntel (Boardroom) — platform-wide list + immersive detail via
         the super-admin /api/admin/boardroom endpoints (08-07-2026). */
      // The built-in demo meeting (calm Home, DES 19-10-2026): sample data, read-only.
      { path: "meetings/demo", element: <DemoMeetingPage /> },
      { path: "meetings", element: <AdminMeetingsPage /> },
      { path: "meetings/:id", element: <AdminMeetingDetailPage /> },
      { path: "analytics", element: <AnalyticsPage /> },
      /* Reports hub — exportable tables (Phase A P0). Distinct from Analytics
         charts. See docs/product/reporting.md (27-07-2026). */
      { path: "reports", element: <AdminReportsPage /> },
      { path: "reports/:reportId", element: <AdminReportsPage /> },
      /* Platform-wide invoices across every tenant — reuses the unified
         /api/invoices feed (superadmin branch). Distinct from Billing, which
         configures rates/costs rather than listing invoices (08-07-2026). */
      { path: "invoices", element: <AdminInvoicesPage /> },
      { path: "billing", element: <BillingPage /> },
      { path: "languages", element: <LanguagesPage /> },
      { path: "banners", element: <BannersPage /> },
      { path: "feedback", element: <FeedbackPage /> },
      { path: "enterprise-inquiries", element: <EnterpriseInquiriesPage /> },
      { path: "activity", element: <ActivityLogPage scope="platform" /> },
      { path: "modules", element: <ModulesPage /> },
      { path: "settings", element: <SettingsPage /> },
      // Links, contact and company details for every app (B23 / W19, 24-10-2026).
      { path: "link-settings", element: <LinkSettingsPage /> },
      // Zapier / Make for every company or one company (B42 / W32, 30-10-2026).
      { path: "integrations", element: <IntegrationSwitchesPage /> },
      /* Per-section rules the AI reads before writing any medico-legal
         report. Its own section rather than a settings row: super admin only,
         and edited far more often than a provider picker (14-09-2026). */
      { path: "report-instructions", element: <ReportInstructionsPage /> },
      /* Self-service profile (avatar, personal details, password) for
         the signed-in super admin. Companies have a separate
         `/company/account` mounted below for the same purpose. */
      { path: "account", element: <ProfilePage /> },
      // Meeting Setup's "enroll your voice" link (13-10-2026).
      { path: "account/voice", element: <VoiceEnrollmentPage /> },
      /* A super admin is a user like any other and has their OWN notes. They
         cannot reach /app (RequireEmployeeApp redirects them) or /company, so
         without these routes they were locked out of their own library.
         (23-08-2026) */
      {
        path: "notes",
        element: (
          <RequireNotesEntitlement redirectTo="/admin">
            <NotesPage />
          </RequireNotesEntitlement>
        ),
      },
      {
        path: "notes/new",
        element: (
          <RequireNotesEntitlement redirectTo="/admin">
            <NoteComposePage />
          </RequireNotesEntitlement>
        ),
      },
      {
        path: "notes/:noteId/edit",
        element: (
          <RequireNotesEntitlement redirectTo="/admin">
            <NoteComposePage />
          </RequireNotesEntitlement>
        ),
      },
      {
        path: "notes/:noteId",
        element: (
          <RequireNotesEntitlement redirectTo="/admin">
            <NoteDetailPage />
          </RequireNotesEntitlement>
        ),
      },
    ],
  },

  // Company Admin space
  {
    path: "/company",
    element: (
      <RequireCompanyAdmin>
        <CompanyAdminLayout />
      </RequireCompanyAdmin>
    ),
    children: [
      { index: true, element: <CompanyAdminDashboardPage /> },
      // The phone's Home, for a company admin too (30-09-2026): on the phone
      // an admin's menu is Home, Dashboard, Sessions, and the app opens on Home.
      { path: "home", element: <HomePage /> },
      // Upcoming + notifications, as the phone (DES 12-10-2026).
      { path: "inbox", element: <InboxPage /> },
      { path: "employees", element: <EmployeesPage /> },
      { path: "activity", element: <ActivityLogPage scope="company" /> },
      /* MeetingIntel (Boardroom) — list + capture + immersive detail.
         Capture pages share employee boardroom UI (28-07-2026). */
      /* The phone's Boardrooms list for the admin too (DES 30-09-2026:
         "desktop app main same hai"). The admin table, with its employee and
         date filters, stays one tap away at /company/meetings/all. */
      { path: "meetings", element: <MeetingsLibraryPage /> },
      /* "All company meetings" in the Boardrooms look (DES 01-10-2026); the
         table page (CompanyMeetingsPage) is no longer routed. */
      { path: "meetings/all", element: <MeetingsLibraryPage key="all" allCompany /> },
      {
        path: "meetings/new",
        element: <BoardroomSetupPage />,
      },
      {
        path: "meetings/record",
        element: <BoardroomRecorderPage />,
      },
      {
        path: "meetings/scheduled",
        element: <ScheduledMeetingsPage />,
      },
      { path: "meetings/demo", element: <DemoMeetingPage /> },
      { path: "meetings/:id", element: <CompanyMeetingDetailPage /> },
      // My to-dos from every meeting, as the phone's (12-10-2026).
      { path: "todos", element: <MyTodosPage /> },
      /* Personal Notes — the admin's OWN notes, not the company's. Mounted
         here as well as under /app because a boardroom is recorded from both
         shells and its owner needs an agenda in the one they are standing in.
         Same page, same user-scoped endpoints. (21-08-2026) */
      {
        path: "notes",
        element: (
          <RequireNotesEntitlement redirectTo="/company">
            <NotesPage />
          </RequireNotesEntitlement>
        ),
      },
      {
        path: "notes/new",
        element: (
          <RequireNotesEntitlement redirectTo="/company">
            <NoteComposePage />
          </RequireNotesEntitlement>
        ),
      },
      {
        path: "notes/:noteId/edit",
        element: (
          <RequireNotesEntitlement redirectTo="/company">
            <NoteComposePage />
          </RequireNotesEntitlement>
        ),
      },
      {
        path: "notes/:noteId",
        element: (
          <RequireNotesEntitlement redirectTo="/company">
            <NoteDetailPage />
          </RequireNotesEntitlement>
        ),
      },
      {
        path: "schedule",
        element: (
          <RequireCompanyFeature feature="interpretation">
            <CompanySchedulePage />
          </RequireCompanyFeature>
        ),
      },
      // Live interpretation from the laptop, a pilot (01-10-2026,
      // docs/product/web-interpretation-plan.md).
      {
        path: "interpret",
        element: (
          <RequireCompanyFeature feature="interpretation">
            <RequireWebInterpretation redirectTo="/company/sessions">
              {import.meta.env.DEV && DEV_INTERPRET_PREVIEW ? (
                <InterpretPreviewPage autoStart />
              ) : (
                <InterpretPage />
              )}
            </RequireWebInterpretation>
          </RequireCompanyFeature>
        ),
      },
      { path: "sessions", element: <CompanySessionsPage /> },
      // A session as its own page, the phone's Session Detail (01-10-2026).
      { path: "sessions/:sessionId", element: <SessionDetailPage scope="company" /> },
      /* Company-scoped analytics with a personal-vs-corporate lens, powered by
         the /api/company/analytics/* endpoints (09-07-2026). */
      { path: "analytics", element: <CompanyAnalyticsPage /> },
      /* Company Reports Phase B P0 — exportable tables. See
         docs/product/reporting.md (27-07-2026). */
      { path: "reports", element: <CompanyReportsPage /> },
      { path: "reports/:reportId", element: <CompanyReportsPage /> },
      { path: "invoices", element: <CompanyInvoicesPage /> },
      { path: "billing", element: <CompanyBillingPage /> },
      /* Medical Assessment — Case Hub + MLR workflow (02-09-2026).
         Company toggle does not hide admin oversight; platform kill switch
         does (09-09-2026). */
      {
        path: "medical-assessment",
        element: (
          <RequireUnlockedFeature feature="medical_assessment">
            <MedicalAssessmentsPage />
          </RequireUnlockedFeature>
        ),
      },
      {
        path: "medical-assessment/:id",
        element: (
          <RequireUnlockedFeature feature="medical_assessment">
            <MedicalAssessmentDetailPage />
          </RequireUnlockedFeature>
        ),
      },
      /* The employee reschedule-request queue was retired 25-09-2026 (a
         booking is one-way for the attendee); old bookmarks land on the
         Schedule diary, where the admin edits a session directly. */
      {
        path: "reschedule-requests",
        element: <Navigate to="/company/schedule" replace />,
      },
      { path: "profile", element: <CompanyProfilePage /> },
      /* Feature on/off toggles for the company's employees (15-07-2026). */
      { path: "features", element: <CompanyFeaturesPage /> },
      /* Settings hub — related live links + coming-soon catalog (30-07-2026). */
      { path: "settings", element: <CompanySettingsPage /> },
      { path: "settings/asana", element: <AsanaIntegrationPage /> },
      // Zapier + Asana in one place (W29, Z1, 27-10-2026).
      { path: "settings/integrations", element: <IntegrationsPage /> },
      /* The MEDYX doctor link is a per-USER credential that every MLR export
         reads, so it sits with the other settings rather than inside one
         assessment's tools (14-09-2026). */
      {
        path: "settings/clinic-software",
        element: (
          <RequireUnlockedFeature feature="medical_assessment">
            <ClinicSoftwareSettingsPage />
          </RequireUnlockedFeature>
        ),
      },
      /* Personal profile editor — same component the super admin uses,
         since the underlying `/api/auth/*` self-service endpoints
         don't care about role. */
      { path: "account", element: <ProfilePage /> },
      // Register your voice, as on the phone (04-10-2026).
      { path: "account/voice", element: <VoiceEnrollmentPage /> },
    ],
  },

  // Employee Boardroom web app — full Meeting Intel capture parity for
  // no-phone / laptop environments (28-07-2026).
  {
    path: "/app",
    element: (
      <RequireEmployeeApp>
        <EmployeeBoardroomLayout />
      </RequireEmployeeApp>
    ),
    children: [
      // The phone's Home, for a user (30-09-2026). Was a redirect to Recordings.
      { index: true, element: <HomePage /> },
      // Upcoming + notifications, as the phone (DES 12-10-2026).
      { path: "inbox", element: <InboxPage /> },
      // The phone's "Boardrooms" list (30-09-2026); was the DataTable page.
      { path: "meetings", element: <MeetingsLibraryPage /> },
      {
        path: "meetings/new",
        element: (
          <RequireBoardroomEntitlement>
            <BoardroomSetupPage />
          </RequireBoardroomEntitlement>
        ),
      },
      {
        path: "meetings/record",
        element: (
          <RequireBoardroomEntitlement>
            <BoardroomRecorderPage />
          </RequireBoardroomEntitlement>
        ),
      },
      {
        path: "meetings/scheduled",
        element: (
          <RequireBoardroomEntitlement>
            <ScheduledMeetingsPage />
          </RequireBoardroomEntitlement>
        ),
      },
      { path: "meetings/demo", element: <DemoMeetingPage /> },
      { path: "meetings/:id", element: <EmployeeMeetingDetailPage /> },
      // My to-dos from every meeting, as the phone's (12-10-2026).
      { path: "todos", element: <MyTodosPage /> },
      // A user's own interpretation sessions (DES 01-10-2026: "a normal user
      // cant see his sessions"). Read-only: no delete, no company invoice.
      { path: "sessions", element: <CompanySessionsPage self /> },
      // Live interpretation from the laptop, a pilot (01-10-2026).
      {
        path: "interpret",
        element: (
          <RequireWebInterpretation redirectTo="/app/sessions">
            {import.meta.env.DEV && DEV_INTERPRET_PREVIEW ? (
              <InterpretPreviewPage autoStart />
            ) : (
              <InterpretPage />
            )}
          </RequireWebInterpretation>
        ),
      },
      // A user's own Diary: sessions booked for them (01-10-2026).
      { path: "schedule", element: <CompanySchedulePage self /> },
      {
        path: "sessions/:sessionId",
        element: <SessionDetailPage scope="company" listPath="/app/sessions" readOnly />,
      },
      {
        path: "notes",
        element: (
          <RequireNotesEntitlement redirectTo="/app/meetings">
            <NotesPage />
          </RequireNotesEntitlement>
        ),
      },
      {
        path: "notes/new",
        element: (
          <RequireNotesEntitlement redirectTo="/app/meetings">
            <NoteComposePage />
          </RequireNotesEntitlement>
        ),
      },
      {
        path: "notes/:noteId/edit",
        element: (
          <RequireNotesEntitlement redirectTo="/app/meetings">
            <NoteComposePage />
          </RequireNotesEntitlement>
        ),
      },
      {
        path: "notes/:noteId",
        element: (
          <RequireNotesEntitlement redirectTo="/app/meetings">
            <NoteDetailPage />
          </RequireNotesEntitlement>
        ),
      },
      { path: "account", element: <ProfilePage /> },
      // Register your voice, as on the phone (04-10-2026).
      { path: "account/voice", element: <VoiceEnrollmentPage /> },
      /* A personal user's Billing: plan, usage, cards, invoices (17-10-2026).
         Each section is a deep link (/app/billing/cards) for emails and
         notifications; a company member sees who pays instead. */
      { path: "billing", element: <Navigate to="/app/billing/plan" replace /> },
      { path: "billing/:section", element: <MyBillingPage /> },
      /* Meeting Intel serves individuals as well as companies, so the Asana
         connector cannot live only under /company. Same page, same user-scoped
         endpoints: a solo user connects their own Asana and picks their own
         project, while a company member inherits the company's destination.
         (05-08-2026) */
      { path: "integrations/asana", element: <AsanaIntegrationPage /> },
      // Zapier + Asana in one place (W29, Z1, 27-10-2026).
      { path: "integrations", element: <IntegrationsPage /> },
    ],
  },

  // Standalone error pages
  { path: "/forbidden", element: <ForbiddenPage /> },
  { path: "*", element: <NotFoundPage /> },

  // Belt-and-braces: any old `/dashboard` deep links go to the redirect
  { path: "/dashboard", element: <Navigate to="/" replace /> },
]);
